import PatternBackground from "@/components/PatternBackground";
import OverlayCard from "@/components/OverlayCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Users, Handshake, Globe } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
const partnerSchema = z.object({
  companyName: z.string().trim().min(1, "Company name is required").max(100, "Company name too long"),
  contactPerson: z.string().trim().min(1, "Contact person is required").max(100, "Name too long"),
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  phone: z.string().max(20, "Phone number too long").optional(),
  partnershipType: z.string().optional(),
  website: z.string().url("Invalid URL").optional().or(z.literal("")),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message too long")
});
const Partners = () => {
  const [formData, setFormData] = useState({
    companyName: "",
    contactPerson: "",
    email: "",
    phone: "",
    partnershipType: "",
    website: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Validate form data
      const validatedData = partnerSchema.parse(formData);
      const response = await fetch(`https://aqehupafavmjsmzdnotd.supabase.co/functions/v1/send-partner-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(validatedData)
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Failed to send partnership inquiry');
      }
      toast.success("Your partnership inquiry has been sent! We'll get back to you soon.");

      // Reset form
      setFormData({
        companyName: "",
        contactPerson: "",
        email: "",
        phone: "",
        partnershipType: "",
        website: "",
        message: ""
      });
    } catch (error) {
      if (error instanceof z.ZodError) {
        const firstError = error.issues[0];
        toast.error(firstError.message);
      } else {
        console.error('Error sending partnership inquiry:', error);
        toast.error("Sorry, there was an error sending your inquiry. Please try again or contact us at hello@kijaniarushaairportlodge.com");
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  const partnerBenefits = [{
    icon: Users,
    title: "Tour Operators",
    description: "Join our network of trusted tour operators and offer your clients an authentic safari lodge experience."
  }, {
    icon: Handshake,
    title: "Business Partnerships",
    description: "Create meaningful partnerships that benefit both our guests and your business objectives."
  }, {
    icon: Globe,
    title: "Global Network",
    description: "Connect with an international network of travel professionals and expand your reach."
  }];
  return <PatternBackground pattern="butterfly">
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Page Title */}
          <div className="text-center mb-16">
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-white mb-6">
              Partner With Us
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Join Kijani Arusha Airport Lodge as a trusted partner and enhance your clients' safari experience with our exceptional hospitality and authentic Tanzanian adventures.
            </p>
            
            {/* Check Availability Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-white font-semibold px-8 py-3">
                <a href="https://kijanisafarilodge.resrequest.com/page/public/availabilitycalendar" target="_blank" rel="noopener noreferrer">
                  Check Live Availability
                </a>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-white text-white hover:bg-white hover:text-foreground font-semibold px-8 py-3">
                <a href="https://kijanisafarilodge.resrequest.com/page/public/availabilitycalendar" target="_blank" rel="noopener noreferrer">
                  View Booking Calendar
                </a>
              </Button>
            </div>
          </div>

          {/* Partner Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {partnerBenefits.map((benefit, index) => <OverlayCard key={index} className="text-center">
                <div className="flex justify-center mb-4">
                  <benefit.icon className="h-12 w-12 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-semibold mb-3 text-foreground">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </OverlayCard>)}
          </div>

          {/* Partnership Inquiry Form */}
          <div className="max-w-4xl mx-auto">
            {/* Additional Check Availability Section */}
            <div className="text-center mb-12">
              <OverlayCard className="bg-primary/10 border-primary/20">
                <h3 className="font-heading text-xl font-bold text-foreground mb-4">
                  For Partners: Quick Availability Check
                </h3>
                <p className="text-muted-foreground mb-6">
                  Access our live availability calendar to check room availability for your clients in real-time.
                </p>
                <Button asChild className="bg-primary hover:bg-primary/90">
                  <a href="https://kijanisafarilodge.resrequest.com/page/public/availabilitycalendar" target="_blank" rel="noopener noreferrer">
                    Access Availability Calendar
                  </a>
                </Button>
              </OverlayCard>
            </div>

            <OverlayCard size="lg">
              <h2 className="font-heading text-3xl font-bold text-foreground mb-6">
                Partnership Inquiry
              </h2>
              <p className="text-muted-foreground mb-6">
                Interested in partnering with Kijani Arusha Airport Lodge? Fill out the form below or contact us directly at{" "}
                <a href="mailto:hello@kijaniarushaairportlodge.com" className="text-primary underline hover:text-primary/80">hello@kijaniarushaairportlodge.com</a>
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="companyName">Company Name *</Label>
                    <Input id="companyName" type="text" value={formData.companyName} onChange={e => setFormData({
                    ...formData,
                    companyName: e.target.value
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="contactPerson">Contact Person *</Label>
                    <Input id="contactPerson" type="text" value={formData.contactPerson} onChange={e => setFormData({
                    ...formData,
                    contactPerson: e.target.value
                  })} required />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" value={formData.email} onChange={e => setFormData({
                    ...formData,
                    email: e.target.value
                  })} required />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input id="phone" type="tel" value={formData.phone} onChange={e => setFormData({
                    ...formData,
                    phone: e.target.value
                  })} />
                  </div>
                </div>

                <div>
                  <Label htmlFor="partnershipType">Partnership Type</Label>
                  <select id="partnershipType" value={formData.partnershipType} onChange={e => setFormData({
                  ...formData,
                  partnershipType: e.target.value
                })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">
                    <option value="">Select Partnership Type</option>
                    <option value="tour-operator">Tour Operator</option>
                    <option value="travel-agency">Travel Agency</option>
                    <option value="corporate">Corporate Partnership</option>
                    <option value="hospitality">Hospitality Partner</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="website">Website/Company URL</Label>
                  <Input id="website" type="url" placeholder="https://" value={formData.website} onChange={e => setFormData({
                  ...formData,
                  website: e.target.value
                })} />
                </div>

                <div>
                  <Label htmlFor="message">Tell us about your business and partnership interest *</Label>
                  <Textarea id="message" rows={6} placeholder="Please describe your company, services, target market, and how you envision partnering with Kijani Safari Lodge..." value={formData.message} onChange={e => setFormData({
                  ...formData,
                  message: e.target.value
                })} required />
                </div>

                <Button type="submit" className="bg-primary hover:bg-primary/90 w-full md:w-auto" disabled={isSubmitting}>
                  {isSubmitting ? "SUBMITTING..." : "SUBMIT PARTNERSHIP INQUIRY"}
                </Button>
              </form>
            </OverlayCard>
          </div>

        </div>
      </div>
    </PatternBackground>;
};
export default Partners;
